import { useEffect, useState } from "react";

/**
 * Branded Arrowlink loading animation.
 * Draws the ↗ arrow contour with an SVG stroke-dashoffset animation,
 * then fades in the wordmark beneath.
 */
const ArrowlinkLoader = () => {
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    // Reveal wordmark almost immediately so the loader feels instant
    // even on routes that hydrate in <300ms.
    const timer = setTimeout(() => setShowText(true), 350);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-5">
      {/* Arrow icon ↗ drawn as SVG with stroke animation */}
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="arrowlink-draw"
        aria-label="Arrowlink loading"
      >
        {/* Arrow body: diagonal line from bottom-left to top-right */}
        <line
          x1="14"
          y1="50"
          x2="50"
          y2="14"
          stroke="hsl(223 89% 60%)"
          strokeWidth="4"
          strokeLinecap="round"
          className="arrow-line"
        />
        {/* Arrow head: top-right lines */}
        <polyline
          points="30,14 50,14 50,34"
          stroke="hsl(223 89% 60%)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="arrow-head"
        />
      </svg>

      {/* Wordmark */}
      <div
        className="flex flex-col items-center transition-all duration-500"
        style={{
          opacity: showText ? 1 : 0,
          transform: showText ? "translateY(0)" : "translateY(6px)",
        }}
      >
        <span
          className="text-lg font-black text-foreground tracking-[0.2em] leading-none"
          style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}
        >
          ARROWLINK
        </span>
        <span className="text-[0.45rem] font-normal text-primary tracking-[0.25em] leading-none mt-1">
          LIVING CONNECTIONS
        </span>
      </div>

      {/* Inline keyframes for the stroke-draw effect */}
      <style>{`
        .arrow-line {
          stroke-dasharray: 52;
          stroke-dashoffset: 52;
          animation: draw-line 0.35s ease-out 0s forwards, pulse-arrow 1.6s ease-in-out 0.4s infinite;
        }
        .arrow-head {
          stroke-dasharray: 40;
          stroke-dashoffset: 40;
          animation: draw-head 0.25s ease-out 0.3s forwards, pulse-arrow 1.6s ease-in-out 0.4s infinite;
        }
        @keyframes draw-line {
          to { stroke-dashoffset: 0; }
        }
        @keyframes draw-head {
          to { stroke-dashoffset: 0; }
        }
        @keyframes pulse-arrow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
      `}</style>
    </div>
  );
};

export default ArrowlinkLoader;
